import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Form,
  TextArea,
  Message,
  Dropdown,
  Icon,
  Header,
  Label,
} from "semantic-ui-react";
import axios from "axios";

// Abuse type options
const abuseOptions = [
  {
    key: "inappropriate",
    value: "Abuse or Inappropriate content",
    text: "Abuse or Inappropriate content",
  },
  { key: "copyright", value: "Copyrighted material", text: "Copyrighted material" },
  { key: "not-research", value: "Not research data", text: "Not research data" },
  { key: "illegal", value: "Illegal content", text: "Illegal content" },
];

const CountryDropdown = ({ value, onChange, error }) => {
  const [countryOptions, setCountryOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get("/api/vocabularies/countries?size=200");
        const hits = response.data.hits?.hits || [];

        const options = hits.map((hit) => ({
          key: hit.id,
          text: hit.title?.en || hit.id,
          value: hit.id,
        }));

        setCountryOptions(options);
      } catch (error) {
        console.error("Error fetching country vocabulary:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  return (
    <Form.Field required error={error ? { content: error, pointing: "below" } : null}>
      <label htmlFor="country">Country</label>
      <Dropdown
        id="country"
        name="country"
        placeholder="Select country"
        fluid
        selection
        search
        loading={loading}
        options={countryOptions}
        value={value}
        onChange={(_, data) => onChange(data.name, data.value)}
      />
    </Form.Field>
  );
};

CountryDropdown.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

export const AbuseReportForm = ({
  formData,
  handleChange,
  error,
  success,
  fieldErrors,
}) => (
  <Form>
    <Message color="red" size="small">
      <Message.Content>
        <Icon name="info circle" size="small" />
        All fields marked with * are mandatory.
      </Message.Content>
    </Message>

    {/* Section 1: Report Content */}
    <Header as="h4" dividing>
      Report Content
    </Header>
    <Form.Field required error={fieldErrors.abuseType}>
      <label htmlFor="abuseType">Type of abuse</label>
      <Dropdown
        id="abuseType"
        name="abuseType"
        placeholder="Select type"
        fluid
        selection
        options={abuseOptions}
        value={formData.abuseType}
        onChange={(_, data) => handleChange(data.name, data.value)}
      />
    </Form.Field>

    <Form.Field required error={!!fieldErrors.message}>
      <label htmlFor="message">Message</label>
      <TextArea
        id="message"
        name="message"
        placeholder="The message content needs to be at least 10 characters."
        value={formData.message}
        onChange={(_, data) => handleChange(data.name, data.value)}
        style={{ minHeight: 100 }}
      />
      {fieldErrors.message && (
        <Label basic color="red" pointing>
          {fieldErrors.message}
        </Label>
      )}
    </Form.Field>

    {/* Section 2: Personal Details */}
    <Header as="h4" dividing>
      Personal Details
    </Header>
    <Form.Group widths="equal">
      <Form.Input
        required
        label="Your full name"
        name="name"
        value={formData.name}
        onChange={(_, data) => handleChange(data.name, data.value)}
        error={fieldErrors.name}
      />
      <Form.Input
        required
        label="Your affiliation"
        name="affiliation"
        value={formData.affiliation}
        onChange={(_, data) => handleChange(data.name, data.value)}
        error={fieldErrors.affiliation}
      />
    </Form.Group>
    <Form.Input
      required
      label="Address"
      name="address"
      value={formData.address}
      onChange={(_, data) => handleChange(data.name, data.value)}
      error={fieldErrors.address}
    />
    <Form.Group widths="equal">
      <Form.Input
        required
        label="City"
        name="city"
        value={formData.city}
        onChange={(_, data) => handleChange(data.name, data.value)}
        error={fieldErrors.city}
      />
      <Form.Input
        required
        label="Zip code"
        name="zip"
        value={formData.zip}
        onChange={(_, data) => handleChange(data.name, data.value)}
        error={fieldErrors.zip}
      />
      <CountryDropdown
        value={formData.country}
        onChange={handleChange}
        error={fieldErrors.country}
      />
    </Form.Group>

    {/* Section 3: Contact Details */}
    <Header as="h4" dividing>
      Contact Details
    </Header>
    <Form.Group widths="equal">
      <Form.Input
        required
        label="Email"
        name="email"
        type="email"
        value={formData.email}
        onChange={(_, data) => handleChange(data.name, data.value)}
        error={fieldErrors.email}
      />
      <Form.Input
        required
        label="Phone number"
        name="phone"
        value={formData.phone}
        onChange={(_, data) => handleChange(data.name, data.value)}
        error={fieldErrors.phone}
      />
    </Form.Group>

    {error && <Message negative>{error}</Message>}
    {success && <Message positive>{success}</Message>}
  </Form>
);

AbuseReportForm.propTypes = {
  formData: PropTypes.shape({
    abuseType: PropTypes.string,
    message: PropTypes.string,
    name: PropTypes.string,
    affiliation: PropTypes.string,
    email: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    zip: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  success: PropTypes.string,
  fieldErrors: PropTypes.object,
};
